import { Link } from 'react-router-dom';
import { MapPin, Award, Zap } from 'lucide-react';

export default function About() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-16 lg:pt-20">
      {/* Hero */}
      <div
        className="relative"
        style={{
          backgroundImage: 'linear-gradient(135deg, rgba(0,0,0,0.90) 0%, rgba(0,0,0,0.65) 50%, rgba(0,0,0,0.90) 100%), url("https://wittmachine.net/wp-content/uploads/2022/08/IMG_04601.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="section-divider" />
          <h1 data-testid="about-heading" className="font-bebas text-6xl lg:text-7xl text-white tracking-wider mb-4">
            OUR STORY
          </h1>
          <p className="font-inter text-base text-zinc-300 max-w-2xl leading-relaxed">
            From 42 inches of concrete in an off-grid cabin to a 27,000 sqft Texas manufacturing facility — the Witt Machine story is about relentless reinvestment, innovation, and proving that American-made can still win on value.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Origin */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <div className="section-divider" />
            <h2 className="font-bebas text-4xl text-white tracking-wider mb-5">FROM HUMBLE BEGINNINGS</h2>
            <p className="font-inter text-sm text-zinc-400 leading-relaxed mb-4">
              Ken Wittekiend started Witt Machine & Tool making four muzzle brakes a day on 42 inches of concrete in an off-grid cabin. No fancy equipment. No investors. Just relentless work and a commitment to making a product that actually worked.
            </p>
            <p className="font-inter text-sm text-zinc-400 leading-relaxed mb-4">
              Creating new products while constantly reinvesting in better equipment led to a 27,000 sqft manufacturing facility in the great state of Texas. From muzzle brakes to fighting suppressors, Witt Machine & Tool has built a reputation for being durable, effective, and exceeding the investment of the customer.
            </p>
            <p className="font-inter text-sm text-zinc-400 leading-relaxed">
              From humble beginnings, Witt Machine & Tool will continue to press the barrier of what is unexpectedly affordable.
            </p>
          </div>
          <div className="bg-[#1e1e1e] border border-white/10 aspect-video overflow-hidden">
            <img
              src="https://wittmachine.net/wp-content/uploads/2022/08/IMG_04601.jpg"
              alt="Witt Machine facility"
              className="w-full h-full object-cover opacity-80"
              onError={e => { e.target.style.display = 'none'; }}
            />
          </div>
        </div>

        {/* Values */}
        <div className="mb-20">
          <div className="section-divider" />
          <h2 className="font-bebas text-4xl text-white tracking-wider mb-8">WHAT DRIVES US</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: MapPin, title: 'Texas Manufactured', desc: 'Every product is manufactured in our Texas facility. American jobs, American steel, American craftsmanship.' },
              { icon: Zap, title: 'Proven Engineering', desc: 'Demonstrated at SHOT Show Industry Day. Tested and used by military, law enforcement, hunters, and competition shooters.' },
              { icon: Award, title: 'Unbeatable Value', desc: 'Pressing the barrier of what is unexpectedly affordable. Premium performance without the premium price tag.' },
            ].map(v => {
              const Icon = v.icon;
              return (
                <div key={v.title} className="bg-[#111111] border border-white/10 p-8">
                  <Icon size={24} className="text-orange-500 mb-4" />
                  <h3 className="font-bebas text-2xl text-white tracking-wider mb-3">{v.title}</h3>
                  <p className="font-inter text-sm text-zinc-400 leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Stats */}
        <div className="bg-[#111111] border border-white/10 p-10 mb-16">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
            {[
              { val: '27,000', sub: 'Square Foot Facility' },
              { val: '60%+', sub: 'Recoil Reduction' },
              { val: 'SOT', sub: 'Class III Manufacturer' },
              { val: 'TX', sub: 'Made in Texas, USA' },
            ].map(s => (
              <div key={s.val} className="text-center">
                <div className="font-bebas text-4xl text-orange-500 tracking-wider">{s.val}</div>
                <div className="font-inter text-xs text-zinc-500 uppercase tracking-widest mt-1">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <h2 className="font-bebas text-4xl text-white tracking-wider mb-4">READY TO EXPERIENCE THE DIFFERENCE?</h2>
          <Link to="/shop" className="inline-block font-bebas text-lg tracking-widest bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 transition-colors">
            SHOP ALL PRODUCTS →
          </Link>
        </div>
      </div>
    </div>
  );
}
